import { EMPTY_LIST } from '../../../../constants/common'
import { RENTAL_PORTAL_APPLICANT_SCREENING_STATES } from '../../../../constants/rentalPortal'
import { APPLICANT_STAGE } from '../../../RentalPortalApplicant'
import { toRentalPortalTimelineItems } from '../../RentalPortalTimelineMarker'
import {
  getIsApplicantDecisionAwaiting,
  getIsApplicantFourEyesRequired,
} from '../RentalPortalApplicantProfile.utils'
import { getApplicantShortDate } from '../RentalPortalApplicantProfileHeaderInfoItems'

import {
  APPLICANT_CHECK_KEY,
  APPLICANT_SCREENING_CLEARED_STATES,
  APPLICANT_SCREENING_NOT_STARTED_STATUS,
  APPLICANT_STAGE_SCREENING_GROUP,
} from './RentalPortalApplicantProfileWidgetStageScreening.constants'

function getApplicantDecisionHint({
  date,
  locale,
  nameKey,
  t,
  timezone,
  user,
}) {
  return user?.full_name && date
    ? t(nameKey, {
        date: getApplicantShortDate({ date, locale, timezone }),
        name: user.full_name,
      })
    : null
}

function getHasApplicantDocumentsProgress(documents) {
  return documents?.required != null
}

function getIsApplicantScreeningStateCleared(screeningState) {
  return APPLICANT_SCREENING_CLEARED_STATES.includes(screeningState)
}

function getHasApplicantSanctionsCheck(applicantData) {
  return (
    applicantData?.policy?.sanctions_screening_required &&
    applicantData?.permissions?.can_view_audit
  )
}

function getApplicantDocumentsCheck({ documents, t }) {
  return {
    hint: getHasApplicantDocumentsProgress(documents)
      ? t('RentalPortal.ApplicantCheckDocumentsProgressKey', {
          required: documents.required,
          verified: documents.verified,
        })
      : null,
    isDone: documents?.complete,
    key: APPLICANT_CHECK_KEY.documents,
    labelKey: 'RentalPortal.ApplicantCheckDocumentsKey',
  }
}

function getApplicantSanctionsCheck({ screeningState, t }) {
  const status =
    RENTAL_PORTAL_APPLICANT_SCREENING_STATES[screeningState] ??
    APPLICANT_SCREENING_NOT_STARTED_STATUS

  return {
    hint: null,
    isDone: getIsApplicantScreeningStateCleared(screeningState),
    key: APPLICANT_CHECK_KEY.sanctions,
    labelKey: 'RentalPortal.ApplicantCheckSanctionsKey',
    statusText: t(status.text),
    statusTextColor: status.textColor,
  }
}

function getApplicantSelectedCheck({ applicantData, locale, t, timezone }) {
  const isSelected = applicantData?.is_selected

  return {
    hint: isSelected
      ? getApplicantDecisionHint({
          date: applicantData.selected_at,
          locale,
          nameKey: 'RentalPortal.ApplicantCheckSelectedByKey',
          t,
          timezone,
          user: applicantData.selected_by,
        })
      : t('RentalPortal.ApplicantCheckSelectedOpenKey'),
    isDone: isSelected,
    key: APPLICANT_CHECK_KEY.selected,
    labelKey: 'RentalPortal.ApplicantCheckSelectedKey',
  }
}

function getApplicantFourEyesHint({ applicantData, locale, t, timezone }) {
  if (applicantData.approved_by) {
    return getApplicantDecisionHint({
      date: applicantData.approved_at,
      locale,
      nameKey: 'RentalPortal.ApplicantCheckFourEyesByKey',
      t,
      timezone,
      user: applicantData.approved_by,
    })
  }

  return getIsApplicantDecisionAwaiting(applicantData)
    ? t('RentalPortal.ApplicantCheckFourEyesOpenKey')
    : null
}

function getApplicantFourEyesCheck({ applicantData, locale, t, timezone }) {
  return {
    hint: getApplicantFourEyesHint({ applicantData, locale, t, timezone }),
    isDone: Boolean(applicantData.approved_by),
    key: APPLICANT_CHECK_KEY.fourEyes,
    labelKey: 'RentalPortal.ApplicantCheckFourEyesKey',
  }
}

// TODO: the consent and credit-check rows come with the credit-check read (IU-987), the ERP handoff row with the handoff read
function getApplicantScreeningChecks({ applicantData, t }) {
  return [
    getApplicantDocumentsCheck({ documents: applicantData?.documents, t }),
    ...(getHasApplicantSanctionsCheck(applicantData)
      ? [
          getApplicantSanctionsCheck({
            screeningState: applicantData.screening_state,
            t,
          }),
        ]
      : EMPTY_LIST),
  ]
}

function getApplicantDecisionChecks({ applicantData, locale, t, timezone }) {
  return [
    getApplicantSelectedCheck({ applicantData, locale, t, timezone }),
    ...(getIsApplicantFourEyesRequired(applicantData)
      ? [getApplicantFourEyesCheck({ applicantData, locale, t, timezone })]
      : EMPTY_LIST),
  ]
}

function getApplicantScreeningCount(checks) {
  return {
    done: checks.filter(check => check.isDone).length,
    total: checks.length,
  }
}

export function getIsApplicantInPool(applicantData) {
  return applicantData?.stage === APPLICANT_STAGE.pool
}

export function getApplicantStageScreeningGroups({
  applicantData,
  locale,
  t,
  timezone,
}) {
  const screeningChecks = getApplicantScreeningChecks({ applicantData, t })

  return [
    {
      checks: toRentalPortalTimelineItems(screeningChecks),
      key: APPLICANT_STAGE_SCREENING_GROUP.screening,
      title: t(
        'RentalPortal.ApplicantProfileScreeningGroupCountKey',
        getApplicantScreeningCount(screeningChecks)
      ),
    },
    {
      checks: toRentalPortalTimelineItems(
        getApplicantDecisionChecks({ applicantData, locale, t, timezone })
      ),
      key: APPLICANT_STAGE_SCREENING_GROUP.decision,
      title: t('RentalPortal.ApplicantProfileDecisionGroupKey'),
    },
  ]
}
